import { useState } from "react";
import { FramePanel } from "@/components/ui/frame";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { Check, AtSign, User } from "lucide-react";
import type { ExclusionType } from "@/stores/settings/organizationSettingsStore";

interface CsvColumnPickerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  csvData: string[][];
  onConfirm: (values: string[], type: ExclusionType) => void;
}

export function CsvColumnPickerDialog({
  open,
  onOpenChange,
  csvData,
  onConfirm,
}: CsvColumnPickerDialogProps) {
  const [selectedColumn, setSelectedColumn] = useState<number | null>(null);
  const [selectedType, setSelectedType] = useState<ExclusionType>("username");

  if (!csvData.length) return null;

  const headers = csvData[0];
  const previewRows = csvData.slice(1, 6);
  const totalRows = csvData.length - 1;

  const reset = () => {
    setSelectedColumn(null);
    setSelectedType("username");
  };

  const handleConfirm = () => {
    if (selectedColumn === null) return;

    const values = csvData
      .slice(1)
      .map((row) => row[selectedColumn]?.trim())
      .filter(Boolean);

    onConfirm(values, selectedType);
    reset();
    onOpenChange(false);
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) reset();
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Upload to Exclusion List</DialogTitle>
          <DialogDescription>
            Pick the type of values, then click the column header that
            contains them. Showing {previewRows.length} of {totalRows} rows.
          </DialogDescription>
        </DialogHeader>

        <Tabs
          value={selectedType}
          onValueChange={(v) => setSelectedType(v as ExclusionType)}
          className="w-fit"
        >
          <TabsList>
            <TabsTrigger value="username" className="gap-1.5">
              <User className="size-3.5" />
              Usernames
            </TabsTrigger>
            <TabsTrigger value="email" className="gap-1.5">
              <AtSign className="size-3.5" />
              Emails
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <FramePanel className="max-h-72 overflow-auto p-0">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent bg-transparent">
                {headers.map((header, i) => (
                  <TableHead
                    key={i}
                    onClick={() => setSelectedColumn(i)}
                    className={cn(
                      "cursor-pointer select-none p-1 px-3 h-10 transition-colors hover:bg-muted hover:text-foreground text-nowrap",
                      selectedColumn === i &&
                      "text-primary hover:text-primary hover:bg-transparent"
                    )}
                  >
                    <div className="flex items-center gap-1.5">
                      {selectedColumn === i && <Check className="size-3.5" />}
                      {header || `Column ${i + 1}`}
                    </div>
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {previewRows.map((row, rowIdx) => (
                <TableRow key={rowIdx}>
                  {headers.map((_, colIdx) => (
                    <TableCell
                      key={colIdx}
                      className={cn(
                        selectedColumn === colIdx && "font-medium", "text-xs"
                      )}
                    >
                      {row[colIdx] || ""}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={headers.length} className="text-center text-xs text-muted-foreground p-2 cursor-default">
                  + {totalRows - previewRows.length} more rows...
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </FramePanel>

        <DialogFooter>
          <Button size="sm" variant="secondary" onClick={() => handleClose(false)}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleConfirm} disabled={selectedColumn === null}>
            Confirm & Upload
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
